import React from 'react'
import {
  Box,
  Paper,
  Button,
  TextField,
  IconButton,
  Typography,
  Autocomplete,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
} from '@mui/material'
import {
  Add as AddIcon,
  CalendarMonth as CalendarIcon,
  TableRows as TableIcon,
} from '@mui/icons-material'

export type DatePreset = 'current_month' | 'last_month' | 'next_month'

export interface DoctorOption {
  id: string
  email?: string
  doctor_profile?: {
    first_name?: string
    last_name?: string
  }
}

export interface DateRangeValue {
  start: Date | null
  end: Date | null
}

export interface StatusOption {
  value: string
  label: string
}

interface ShiftsFiltersBarProps {
  selectedInstitution: string
  availableInstitutions: Array<{ id: string; name: string }>
  onInstitutionChange: (institutionId: string) => void

  showDoctorFilter?: boolean
  doctorOptions?: DoctorOption[]
  doctorValue?: DoctorOption | null
  doctorInputValue?: string
  loadingDoctorOptions?: boolean
  onDoctorInputChange?: (value: string, reason: 'input' | 'clear' | 'reset') => void
  onDoctorChange?: (value: DoctorOption | null) => void

  selectedStatuses: string[]
  statusOptions: StatusOption[]
  onStatusToggle: (status: string) => void

  selectedModalities: string[]
  availableModalities: string[]
  onModalityToggle: (modality: string) => void

  dateRange: DateRangeValue
  datePreset: DatePreset | null
  onDateRangeChange: (range: DateRangeValue) => void
  onDatePresetChange: (preset: DatePreset | null) => void

  // Calendar month/year navigation
  calendarMonth: number
  calendarYear: number
  onCalendarMonthChange: (month: number) => void
  onCalendarYearChange: (year: number) => void

  onClearFilters: () => void
  
  // View toggle (Calendar / Table)
  activeView: 'calendar' | 'table'
  onViewChange: (view: 'calendar' | 'table') => void
  
  // Add shift button (like "Adaugă Pacient" in Worklist)
  canAddShift?: boolean
  onAddShift?: () => void
  addButtonLabel?: string
  shiftType?: 'doctor' | 'technician'
}

const ShiftsFiltersBar: React.FC<ShiftsFiltersBarProps> = React.memo(({
  selectedInstitution,
  availableInstitutions,
  onInstitutionChange,
  showDoctorFilter = false,
  doctorOptions = [],
  doctorValue = null,
  doctorInputValue = '',
  loadingDoctorOptions = false,
  onDoctorInputChange,
  onDoctorChange,
  selectedStatuses,
  statusOptions,
  onStatusToggle,
  selectedModalities,
  availableModalities,
  onModalityToggle,
  dateRange,
  datePreset,
  onDateRangeChange,
  onDatePresetChange,
  calendarMonth,
  calendarYear,
  onCalendarMonthChange,
  onCalendarYearChange,
  onClearFilters,
  activeView,
  onViewChange,
  canAddShift = false,
  onAddShift,
  addButtonLabel,
  shiftType = 'doctor',
}) => {
  const formatDateForInput = (date: Date | null): string => {
    if (!date) return ''
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
  }

  const parseDateFromInput = (dateStr: string): Date | null => {
    if (!dateStr) return null
    const parts = dateStr.split('-')
    if (parts.length !== 3) return null
    const year = parseInt(parts[0], 10)
    const month = parseInt(parts[1], 10) - 1
    const day = parseInt(parts[2], 10)
    if (Number.isNaN(day) || Number.isNaN(month) || Number.isNaN(year)) return null
    return new Date(year, month, day)
  }

  const getMonthRange = (offset: number): DateRangeValue => {
    const base = new Date()
    const month = base.getMonth() + offset
    const start = new Date(base.getFullYear(), month, 1, 0, 0, 0, 0)
    const end = new Date(base.getFullYear(), month + 1, 0, 23, 59, 59, 999)
    return { start, end }
  }

  const handlePresetChange = (preset: DatePreset) => {
    onDatePresetChange(preset)
    let range: DateRangeValue
    
    switch (preset) {
      case 'last_month':
        range = getMonthRange(-1)
        break
      case 'next_month':
        range = getMonthRange(1)
        break
      case 'current_month':
      default:
        range = getMonthRange(0)
        break
    }
    
    onDateRangeChange(range)
    
    // Update calendar month/year to match
    if (range.start) {
      onCalendarMonthChange(range.start.getMonth())
      onCalendarYearChange(range.start.getFullYear())
    }
  }

  const statusColors: Record<string, string> = {
    planificata: '#1976d2',
    in_curs: '#f59e0b',
    finalizata: '#2e7d32',
  }

  return (
    <Paper
      elevation={0}
      sx={{
        mb: 1.2,
        p: 0.6,
        border: '1px solid',
        borderColor: 'divider',
        backgroundColor: 'background.paper',
      }}
    >
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            md: canAddShift 
              ? '240px 64px 260px minmax(280px, 380px) auto'
              : '240px 64px 260px minmax(320px, 420px)',
          },
          gap: 0.6,
          alignItems: 'stretch',
          mb: 0.6,
        }}
      >
        <Box
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            p: 0.5,
            display: 'flex',
            flexDirection: 'column',
            gap: 0.5,
            minHeight: 62,
          }}
        >
          <FormControl size="small" fullWidth>
            <InputLabel sx={{ fontSize: '0.75rem' }}>Instituție</InputLabel>
            <Select
              value={selectedInstitution}
              onChange={(e) => onInstitutionChange(e.target.value)}
              label="Instituție"
              sx={{
                fontSize: '0.75rem',
                height: 28,
                backgroundColor: '#2d3748',
                color: '#fff',
                '& .MuiSelect-select': { py: 0.25, px: 1 },
                '& .MuiOutlinedInput-notchedOutline': { borderColor: '#4a5568' },
                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#718096' },
                '& .MuiSvgIcon-root': { color: '#fff' },
              }}
            >
              <MenuItem value="all" sx={{ fontSize: '0.75rem' }}>
                Toate instituțiile
              </MenuItem>
              {availableInstitutions.map((institution) => (
                <MenuItem key={institution.id} value={institution.id} sx={{ fontSize: '0.75rem' }}>
                  {institution.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {showDoctorFilter && (
            <Autocomplete
              size="small"
              options={doctorOptions}
              value={doctorValue}
              inputValue={doctorInputValue}
              onInputChange={(_event, value, reason) => {
                onDoctorInputChange?.(value, reason as any)
              }}
              onChange={(_event, value) => {
                onDoctorChange?.(value as DoctorOption | null)
              }}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              getOptionLabel={(option) => {
                if (typeof option === 'string') return option
                const name = `${option.doctor_profile?.first_name || ''} ${option.doctor_profile?.last_name || ''}`.trim()
                return name || option.email || ''
              }}
              loading={loadingDoctorOptions}
              noOptionsText="Nu s-au găsit medici"
              sx={{
                '& .MuiAutocomplete-paper': {
                  backgroundColor: '#2d3748',
                  color: '#fff',
                },
                '& .MuiAutocomplete-listbox': {
                  fontSize: '0.75rem',
                },
              }}
              renderOption={(props, option) => {
                const name = `${option.doctor_profile?.first_name || ''} ${option.doctor_profile?.last_name || ''}`.trim()
                const label = name || option.email || ''
                return (
                  <li {...props} style={{ fontSize: '0.75rem' }}>
                    {label}
                  </li>
                )
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={shiftType === 'technician' ? 'Angajat' : 'Medic'}
                  placeholder={shiftType === 'technician' ? 'Caută angajat...' : 'Caută medic...'}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingDoctorOptions ? <CircularProgress size={16} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                  sx={{
                    '& .MuiInputLabel-root': {
                      fontSize: '0.75rem',
                      color: '#fff',
                    },
                    '& .MuiInputBase-root': {
                      height: 28,
                      backgroundColor: '#2d3748',
                      color: '#fff',
                      fontSize: '0.75rem',
                    },
                    '& .MuiInputBase-input': { py: 0.25, px: 1 },
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#4a5568' },
                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#718096' },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#718096' },
                    '& .MuiSvgIcon-root': { color: '#fff' },
                  }}
                />
              )}
            />
          )}
        </Box>

        {/* View Toggle: Calendar / Table - vertical like refresh/sync in worklist */}
        <Box
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            p: 0.5,
            display: 'flex',
            flexDirection: 'column',
            gap: 0.5,
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: 62,
          }}
        >
          <Tooltip title="Vizualizare Calendar">
            <IconButton
              size="small"
              onClick={() => onViewChange('calendar')}
              sx={{
                width: 28,
                height: 28,
                border: '2px solid',
                borderColor: activeView === 'calendar' ? 'primary.main' : 'divider',
                borderRadius: 1,
                backgroundColor: activeView === 'calendar' ? 'primary.main' : 'transparent',
                color: activeView === 'calendar' ? '#fff' : 'text.secondary',
                '&:hover': {
                  backgroundColor: activeView === 'calendar' ? 'primary.dark' : 'action.hover',
                },
              }}
            >
              <CalendarIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Vizualizare Tabel">
            <IconButton
              size="small"
              onClick={() => onViewChange('table')}
              sx={{
                width: 28,
                height: 28,
                border: '2px solid',
                borderColor: activeView === 'table' ? 'primary.main' : 'divider',
                borderRadius: 1,
                backgroundColor: activeView === 'table' ? 'primary.main' : 'transparent',
                color: activeView === 'table' ? '#fff' : 'text.secondary',
                '&:hover': {
                  backgroundColor: activeView === 'table' ? 'primary.dark' : 'action.hover',
                },
              }}
            >
              <TableIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        </Box>

        <Box
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            p: 0.5,
            minHeight: 62,
            overflow: 'hidden',
          }}
        >
          <Typography variant="caption" sx={{ display: 'block', mb: 0.25, color: 'text.secondary' }}>
            Status
          </Typography>
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 0.5,
              maxHeight: 48,
              overflow: 'hidden',
            }}
          >
            {statusOptions.map((status) => {
              const isSelected = selectedStatuses.includes(status.value)
              const statusColor = statusColors[status.value] || '#616161'
              return (
                <Button
                  key={status.value}
                  size="small"
                  variant={isSelected ? 'contained' : 'outlined'}
                  onClick={() => onStatusToggle(status.value)}
                  sx={{
                    minWidth: 'auto',
                    height: 22,
                    px: 0.75,
                    py: 0,
                    fontSize: '0.68rem',
                    textTransform: 'none',
                    borderColor: isSelected ? statusColor : 'divider',
                    backgroundColor: isSelected ? statusColor : 'transparent',
                    color: isSelected ? '#fff' : 'text.secondary',
                    fontWeight: isSelected ? 600 : 400,
                  }}
                >
                  {status.label}
                </Button>
              )
            })}
          </Box>
        </Box>

        <Box
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            p: 0.5,
            minHeight: 62,
            overflow: 'hidden',
          }}
        >
          <Typography variant="caption" sx={{ display: 'block', mb: 0.25, color: 'text.secondary' }}>
            Modalități
          </Typography>
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 0.5,
              maxHeight: 48,
              overflow: 'hidden',
            }}
          >
            {availableModalities.map((modality) => {
              const isSelected = selectedModalities.includes(modality)
              return (
                <Button
                  key={modality}
                  size="small"
                  variant={isSelected ? 'contained' : 'outlined'}
                  onClick={() => onModalityToggle(modality)}
                  sx={{
                    minWidth: 28,
                    height: 22,
                    px: 0.5,
                    py: 0,
                    fontSize: '0.68rem',
                    textTransform: 'none',
                    borderColor: isSelected ? '#0288d1' : 'divider',
                    backgroundColor: isSelected ? '#0288d1' : 'transparent',
                    color: isSelected ? '#fff' : 'text.secondary',
                    fontWeight: isSelected ? 600 : 400,
                  }}
                >
                  {modality}
                </Button>
              )
            })}
          </Box>
        </Box>

        {/* Bloc: Adaugă Gardă (ca "Adaugă Pacient" în Worklist) */}
        {canAddShift && (
          <Box
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              p: 0.5,
              minHeight: 62,
              width: 240,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
            }}
          >
            <Tooltip title={addButtonLabel || 'Adaugă gardă nouă'}>
              <Button
                fullWidth
                size="small"
                variant="outlined"
                startIcon={<AddIcon sx={{ fontSize: 16 }} />}
                onClick={onAddShift}
                sx={{
                  height: 28,
                  fontSize: '0.75rem',
                  textTransform: 'none',
                }}
              >
                {addButtonLabel || 'Adaugă Gardă'}
              </Button>
            </Tooltip>
          </Box>
        )}
      </Box>

      <Box sx={{ mt: 1.5, display: 'flex', gap: 0.75, alignItems: 'center', flexWrap: 'wrap' }}>
        {/* Month Selector */}
        <FormControl size="small" sx={{ minWidth: 130 }}>
          <InputLabel 
            sx={{ 
              fontSize: '0.75rem',
              color: '#a0aec0',
              '&.Mui-focused': { color: '#a0aec0' },
            }}
          >
            Lună
          </InputLabel>
          <Select
            value={calendarMonth}
            onChange={(e) => {
              const newMonth = Number(e.target.value)
              onCalendarMonthChange(newMonth)
              onDatePresetChange(null)
            }}
            label="Lună"
            sx={{
              fontSize: '0.825rem',
              height: 28,
              backgroundColor: '#1a202c',
              color: '#a0aec0',
              '& .MuiSelect-select': { py: 0.25, px: 1.25 },
              '& .MuiOutlinedInput-notchedOutline': { borderColor: '#4a5568' },
              '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#718096' },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#3b82f6' },
              '& .MuiSvgIcon-root': { color: '#a0aec0' },
            }}
          >
            {[
              'Ianuarie', 'Februarie', 'Martie', 'Aprilie', 'Mai', 'Iunie',
              'Iulie', 'August', 'Septembrie', 'Octombrie', 'Noiembrie', 'Decembrie'
            ].map((month, index) => (
              <MenuItem key={index} value={index} sx={{ fontSize: '0.75rem' }}>{month}</MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Year Selector */}
        <FormControl size="small" sx={{ minWidth: 90 }}>
          <InputLabel 
            sx={{ 
              fontSize: '0.75rem',
              color: '#a0aec0',
              '&.Mui-focused': { color: '#a0aec0' },
            }}
          >
            An
          </InputLabel>
          <Select
            value={calendarYear}
            onChange={(e) => {
              const newYear = Number(e.target.value)
              onCalendarYearChange(newYear)
              onDatePresetChange(null)
            }}
            label="An"
            sx={{
              fontSize: '0.825rem',
              height: 28,
              backgroundColor: '#1a202c',
              color: '#a0aec0',
              '& .MuiSelect-select': { py: 0.25, px: 1.25 },
              '& .MuiOutlinedInput-notchedOutline': { borderColor: '#4a5568' },
              '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#718096' },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#3b82f6' },
              '& .MuiSvgIcon-root': { color: '#a0aec0' },
            }}
          >
            {Array.from({ length: 10 }, (_, i) => new Date().getFullYear() - 2 + i).map(year => (
              <MenuItem key={year} value={year} sx={{ fontSize: '0.75rem' }}>{year}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <ToggleButtonGroup
          size="small"
          exclusive
          value={datePreset}
          onChange={(_event, value) => {
            if (!value) return
            handlePresetChange(value)
          }}
          sx={{
            '& .MuiToggleButton-root': {
              height: 28,
              px: 1,
              fontSize: '0.7rem',
              textTransform: 'none',
            },
          }}
        >
          <ToggleButton value="last_month">Luna trecută</ToggleButton>
          <ToggleButton value="current_month">Luna curentă</ToggleButton>
          <ToggleButton value="next_month">Luna viitoare</ToggleButton>
        </ToggleButtonGroup>

        <Box sx={{ flex: 1 }} />

        <Button
          size="small"
          variant="text"
          onClick={onClearFilters}
          sx={{
            height: 28,
            px: 0.75,
            py: 0,
            fontSize: '0.7rem',
            textTransform: 'none',
            color: 'text.secondary',
            minWidth: 'auto',
            whiteSpace: 'nowrap',
            '&:hover': {
              backgroundColor: 'rgba(211, 47, 47, 0.08)',
              color: 'error.main',
            },
          }}
        >
          Șterge filtre
        </Button>
      </Box>
    </Paper>
  )
})

export default ShiftsFiltersBar
